import React, { useState, useEffect } from "react";

import classnames from "classnames";
import { useValidatePlanSelectionMutation } from "EmployerApplication/api";
import { setFilter } from "QuotePage/Actions";
import { getRecommendationMode } from "QuotePage/app/compactQuotePage/Selectors";
import { InStateParticipationModal } from "QuotePage/Components/InStateParticipationModal";
import { RenewalCarrierChangeModal } from "QuotePage/Components/RenewalCarrierChangeModal";
import { getPlanPosition, getPayrollLead } from "QuotePage/Selectors";
import { planDetailsURL, isRenewal } from "QuotePage/urls";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { trackStayWithDefaultRenewal } from "Renewal/utils";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import CobaltButton, { RouterButton, LinkButton } from "Cobalt/Button";
import { useModal } from "Cobalt/Modal";
import { Popover } from "Cobalt/Popover";
import { addPlanToCart } from "Common/CartService";
import { getPlanById } from "Common/PlanLoader/Selectors";
import SIOverlay from "Common/SIOverlay";
import Button from "Theme/Button";

import type { MedicalPlanPrice } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("QuotePagePlanCard");

export type PlanCardRole =
  /** Default Renewal flow */
  | "recommended"

  /** Already enrolled and wanting to view details */
  | "enrolled"
  | string;

export type PlanCardActionProps = {
  planId: MedicalPlanPrice.id;
  className?: string;
  onClick?: (plan: MedicalPlanPrice) => void;
  children?: any;
  disabled?: boolean;
};

const ANALYTICS_LOCATION = "expanding_plan_card";

const buildTrackingData = (recommendationMode, position) => ({
  location: ANALYTICS_LOCATION,
  custom_recommendation: recommendationMode === "recommendation_mode",
  recommendationMode,
  position,
  renewal: isRenewal(),
});
export const StartApplicationButton = ({ planId, className, onClick, children, ...props }: PlanCardActionProps) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const dispatch = useDispatch();
  const recommendationMode = useSelector(getRecommendationMode);
  const position = useSelector((state) => getPlanPosition(state, plan.id));
  const payrollLead = useSelector((state) => getPayrollLead(state));

  const startStandardApplication = () => {
    dispatch(
      addPlanToCart({
        planType: plan.type,
        plan,
        tracking: buildTrackingData(recommendationMode, position),
        shouldClearDentalVision: true,
        continueToCart: true,
        shouldClearLife: true,
      }),
    );
  };

  const handleClick = (e) => {
    e.stopPropagation();
    startStandardApplication();
    if (onClick) onClick(plan);
  };
  if (payrollLead?.partner) {
    return (
      <LinkButton className={classnames(bem("Button", "Primary"), className)} size="medium" {...props} href={`/${payrollLead.partner}/router/payroll-prospect`}>
        Get Started
      </LinkButton>
    );
  }
  return (
    <CobaltButton color="primary" size="medium" className={className} {...props} onClick={handleClick}>
      {children || "Get Started"}
    </CobaltButton>
  );
};

export const ChangeRenewalButton = ({ planId, className, onClick, children, ...props }: PlanCardActionProps) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const dispatch = useDispatch();
  const recommendationMode = useSelector(getRecommendationMode);
  const position = useSelector((state) => getPlanPosition(state, plan.id));
  const [validatePlanSelection, { data: planRequirements, isUninitialized }] = useValidatePlanSelectionMutation();
  const carrierChangeModal = useModal();
  const inStateParticipationModal = useModal();

  const [warnings, setWarnings] = useState({ carrierChange: false, inStateParticipation: false });

  // Closing one modal closes them all
  const handleCloseAll = () => {
    carrierChangeModal.hide();
    inStateParticipationModal.hide();
  };

  const handleContinue = () => {
    dispatch(
      addPlanToCart({
        planType: plan.type,
        plan,
        tracking: buildTrackingData(recommendationMode, position),
        shouldClearDentalVision: false,
        continueToCart: true,
        shouldClearLife: false,
      }),
    );
  };

  // Fetch and display validation warnings / confirmation prompts if needed
  // Warnings remain valid until the user explicitly confirms intent to proceed
  const handleStart = async () => {
    try {
      const { carrierChange, inStateParticipation } = await validatePlanSelection({ planId }).unwrap();
      const initialWarnings = {
        carrierChange,
        inStateParticipation: !inStateParticipation.valid,
      };
      setWarnings(initialWarnings);
      if (!initialWarnings.carrierChange && !initialWarnings.inStateParticipation) {
        handleContinue();
      }
    } catch (error) {
      console.error("Validation failed:", error);
      handleContinue();
    }
  };

  // If warnings exist, show one warning after another until they are all acknowledged
  useEffect(() => {
    if (warnings.carrierChange) {
      carrierChangeModal.show();
    } else if (warnings.inStateParticipation) {
      inStateParticipationModal.show();
    }
  }, [warnings, isUninitialized]);

  return (
    <>
      <Button color="primary" size="medium" {...props} onClick={handleStart}>
        {children || "Get Started"}
      </Button>
      <RenewalCarrierChangeModal
        {...carrierChangeModal}
        hide={handleCloseAll}
        onConfirm={() => {
          setWarnings((prevState) => ({ ...prevState, carrierChange: false }));
          carrierChangeModal.hide();
          if (!warnings.inStateParticipation) {
            handleContinue();
          }
        }}
      />
      <InStateParticipationModal
        {...inStateParticipationModal}
        {...planRequirements?.inStateParticipation}
        hide={handleCloseAll}
        onConfirm={() => {
          setWarnings((prevState) => ({ ...prevState, inStateParticipation: false }));
          inStateParticipationModal.hide();
          if (!warnings.carrierChange) {
            handleContinue();
          }
        }}
        onFilter={() => {
          dispatch(setFilter("out_of_state", { include: [true] }));
          inStateParticipationModal.hide();
        }}
      />
    </>
  );
};

export const MoreDetailsButton = ({ planId, className, onClick, children, ...props }: PlanCardActionProps) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const recommendationMode = useSelector(getRecommendationMode);
  const position = useSelector((state) => getPlanPosition(state, planId));

  const handleClick = (e) => {
    e.stopPropagation();
    AnalyticsTracking.trackEvent("plan_details_opened", buildTrackingData(recommendationMode, position));

    if (onClick) onClick(plan);
  };

  return (
    <RouterButton color="secondary" size="medium" to={planDetailsURL({ params: { planID: planId } })} {...props} onClick={handleClick}>
      {children || "View Plan Details"}
    </RouterButton>
  );
};

export const ContinueApplicationButton = ({ planId, className, children, onClick, ...props }: PlanCardActionProps) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const dispatch = useDispatch();
  const recommendationMode = useSelector(getRecommendationMode);
  const position = useSelector((state) => getPlanPosition(state, plan.id));

  const handleClick = (e) => {
    e.stopPropagation();
    // Extra tracking included for recommended plans
    trackStayWithDefaultRenewal({ location: "quote_page_plan_card" });
    dispatch(
      addPlanToCart({
        planType: plan.type,
        plan,
        tracking: buildTrackingData(recommendationMode, position),
        shouldClearDentalVision: true,
        continueToCart: true,
        shouldClearLife: false,
      }),
    );

    if (onClick) onClick(plan);
  };
  return (
    <Button className={classnames(bem("Button", "Primary"), className)} size="medium" {...props} onClick={handleClick}>
      {children || "Continue"}
    </Button>
  );
};

export const DefaultRenewalButton = ({ planId, className, children, onClick, ...props }: PlanCardActionProps) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const dispatch = useDispatch();
  const recommendationMode = useSelector(getRecommendationMode);
  const position = useSelector((state) => getPlanPosition(state, plan.id));

  const handleClick = (e) => {
    e.stopPropagation();
    dispatch(
      addPlanToCart({
        planType: plan.type,
        plan,
        tracking: buildTrackingData(recommendationMode, position),
        shouldClearDentalVision: false,
        continueToCart: true,
        shouldClearLife: false,
      }),
    );

    if (onClick) onClick(plan);
  };
  return (
    <Button className={classnames(bem("Button", "Primary"), className)} size="medium" {...props} onClick={handleClick}>
      {children || "Renew Plan"}
    </Button>
  );
};
export const CurrentPlanFakeButton = ({ planId, className, children, ...props }: PlanCardActionProps) => (
  <SIOverlay
    placement="top"
    overlayContent={
      <Popover showBackdrop={false} id="current_plan">
        This is your existing plan and cannot be chosen for your renewal. We show it here for comparison purposes.
      </Popover>
    }
    overlayTarget={
      <Button className={classnames(bem("Button", "Primary"), className)} size="medium" {...props} disabled>
        {children || "Current Plan"}
      </Button>
    }
  />
);

export const MoreDetailsLinkButton = ({ planId, children, onClick, ...props }: PlanCardActionProps) => {
  const plan = useSelector((state) => getPlanById(state, planId));
  const recommendationMode = useSelector(getRecommendationMode);
  const position = useSelector((state) => getPlanPosition(state, planId));

  const handleClick = (e) => {
    e.stopPropagation();
    AnalyticsTracking.trackEvent("plan_details_opened", buildTrackingData(recommendationMode, position));

    // Since PlanDetials modal and its sections have not rendered yet, the browser cannot
    // scroll to an element id when provided a hash. Quick fix is to scroll to the element
    // via location.hash on the next process.tick, where the modal and section DO exist
    // FUTURE: update PlanDetails modal to scrollto location.hash if provided on initial render
    setImmediate(() => {
      window.location.hash = "#finePrint";
    });
    if (onClick) onClick(plan);
  };

  return (
    <Link {...props} to={planDetailsURL({ params: { planID: planId } })} onClick={handleClick}>
      {children || "View more plan details"}
    </Link>
  );
};
